import type { Schema } from '@/api/client'

/**
 * The wizard's step metadata: the copy and the URL for each of the six.
 *
 * The **order** is not here — it comes from the server, on every response, in
 * `state.steps`. That is deliberate: `app/onboarding/steps.py` decides what
 * "step 4 of 6" means and the database has a `CHECK` constraint to match, so a
 * second ordering in the SPA would be a third opinion nobody reconciles. This
 * table is keyed by step and holds only what the server has no business knowing:
 * what the screen is called and where it lives in the router.
 */

export type OnboardingState = Schema<'OnboardingState'>
export type StepOut = Schema<'StepOut'>
export type ConnectionOut = Schema<'ConnectionOut'>
export type SuggestionOut = Schema<'SuggestionOut'>

export type StepKey = 'site' | 'search_console' | 'linkedin' | 'sender' | 'keywords' | 'brand_voice'

export interface StepMeta {
  key: StepKey
  /** The last URL segment. Hyphens, because `search_console` is not a path. */
  slug: string
  /** What the rail calls it — short enough to fit six across at 390px. */
  short: string
  /** The heading on the step's own screen. */
  title: string
  /** What the tenant loses by skipping it. Read by the resume screen. */
  consequence: string
  /** How a finished step reads on the resume list — "Site added", not "Site done". */
  settled: string
}

/**
 * The four tones S-D2 offers, as the tenant chose them.
 *
 * Here rather than in `BrandVoiceStep` because the resume screen shows the
 * chosen voice back, and a slug rendered with its underscores stripped —
 * "professional warm" — reads as a leaked database value rather than as the
 * thing they picked.
 */
export const TONE_LABELS: Record<string, string> = {
  professional_warm: 'Professional and warm',
  direct_technical: 'Direct and technical',
  friendly_casual: 'Friendly and casual',
  formal: 'Formal',
}

export const STEP_META: Record<StepKey, StepMeta> = {
  site: {
    key: 'site',
    slug: 'site',
    short: 'Site',
    title: 'What site should we work on?',
    consequence: 'Nothing can be audited, tracked or written without it.',
    settled: 'Site added',
  },
  search_console: {
    key: 'search_console',
    slug: 'search-console',
    short: 'Search Console',
    title: 'Connect Google Search Console',
    consequence: 'Without this the dashboard has no click or impression data.',
    settled: 'Search Console connected',
  },
  linkedin: {
    key: 'linkedin',
    slug: 'linkedin',
    short: 'LinkedIn',
    title: 'Connect LinkedIn for lead discovery',
    consequence: 'Optional — lead discovery only. SEO works without it.',
    settled: 'LinkedIn connected',
  },
  sender: {
    key: 'sender',
    slug: 'sender',
    short: 'Sender',
    title: 'Who are emails from?',
    consequence: "Outreach can't send until this is done.",
    settled: 'Sender set up',
  },
  keywords: {
    key: 'keywords',
    slug: 'keywords',
    short: 'Keywords',
    title: 'Which searches should we track?',
    consequence: 'Rank tracking runs with nothing to track.',
    settled: 'Keywords selected',
  },
  brand_voice: {
    key: 'brand_voice',
    slug: 'brand-voice',
    short: 'Voice',
    title: 'How should we sound?',
    consequence: 'Drafts are written in the default voice.',
    settled: 'Brand voice set',
  },
}

/** Every step, in the order the server reported. Unknown keys are dropped, not guessed at. */
export function metaFor(step: string): StepMeta | null {
  // The index signature says every key resolves; the server may send a step this
  // build has never heard of, which is what a deploy mid-release looks like.
  return (STEP_META as Partial<Record<string, StepMeta>>)[step] ?? null
}

export function pathFor(step: string): string {
  const meta = metaFor(step)
  return meta ? `/onboarding/${meta.slug}` : '/onboarding'
}

/** The step a URL segment names, or null. The inverse of `pathFor`. */
export function stepForSlug(slug: string | undefined): StepKey | null {
  if (!slug) return null
  const found = Object.values(STEP_META).find((meta) => meta.slug === slug)
  return found?.key ?? null
}

/** The `connections` row for a provider, or null when nothing has been tried. */
export function connectionFor(state: OnboardingState, provider: string): ConnectionOut | undefined {
  return state.connections.find((row) => row.provider === provider)
}

export function stepStatus(state: OnboardingState, key: StepKey): StepOut | undefined {
  return state.steps.find((row) => row.step === key)
}

/**
 * A `detail` payload, never undefined.
 *
 * `detail` is a Pydantic field with a `default_factory`, which OpenAPI renders as
 * optional — so the generated type says `| undefined` for something the server
 * always sends as at least `{}`. One helper rather than a `?? {}` at every read,
 * because the ones that get forgotten are the ones inside a conditional.
 */
export function detailOf(
  row: { detail?: Record<string, unknown> } | undefined,
): Record<string, unknown> {
  return row?.detail ?? {}
}

/**
 * Steps that are not **done** — which is not the same as `progress.outstanding`.
 *
 * The server's `outstanding` answers "where will the wizard stop next", so a
 * skipped step is settled and drops out of it. *This* answers "what is not
 * configured", where a skipped step is precisely the thing to say. S-D2 draws
 * both: the progress meter counts settled steps, and the finished screen's
 * "Waiting on you" panel lists the skipped ones by name.
 *
 * Conflating the two is what made the summary say "Nothing — every step is
 * done" to a tenant who had just skipped three, which is how this was found.
 */
export function notConfigured(state: OnboardingState): StepOut[] {
  return state.steps.filter((row) => row.status !== 'done')
}

/** How much time is left, phrased as S-D2 phrases it. Roughly a minute a step. */
export function timeRemaining(state: OnboardingState): string {
  const left = state.progress.outstanding.length
  if (left === 0) return 'Almost done'
  if (left === 1) return 'About a minute left'
  return `About ${String(left)} minutes left`
}
